/**
 * Custom hook for managing OpenLayers measurement interactions.
 *
 * Handles Draw interactions for distance (LineString) and area (Polygon) measurement,
 * real-time cursor tracking, and coordinate click capture.
 *
 * Validates: Requirements 19.1, 19.2, 19.3, 19.4, 20.1, 20.2, 20.3, 21.1, 21.2
 */
import { useEffect, useRef, useCallback, type RefObject } from 'react';
import type Map from 'ol/Map';
import type MapBrowserEvent from 'ol/MapBrowserEvent';
import Draw from 'ol/interaction/Draw';
import LineString from 'ol/geom/LineString';
import Polygon from 'ol/geom/Polygon';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Style, Fill, Stroke, Circle as CircleStyle } from 'ol/style';
import { toLonLat } from 'ol/proj';
import type { Coordinate } from 'ol/coordinate';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  addDistancePoint,
  addAreaPoint,
  setCursorCoordinates,
  setClickedCoordinate,
  updateCurrentDistance,
  updateCurrentArea,
  updateCurrentPerimeter,
  finishMeasurement,
} from '../store/slices/measurementSlice';
import { stopDigitization } from '../store/slices/digitizationSlice';
import { setActiveTool as setDrawingTool } from '../store/slices/drawingSlice';
import { deactivateNodeEditing, deactivateSplitTool } from '../store/slices/geometryEditingSlice';
import { measureDistance, measureArea, measurePerimeter } from '../services/MeasurementService';

interface UseMeasurementInteractionOptions {
  /** Live map value; preferred so restored modes bind after map initialization. */
  map?: Map | null;
  /** Backward-compatible ref for existing consumers. */
  mapRef?: RefObject<Map | null>;
}

const measurementStyle = new Style({
  fill: new Fill({ color: 'rgba(255, 165, 0, 0.2)' }),
  stroke: new Stroke({ color: '#ff6600', width: 2, lineDash: [8, 4] }),
  image: new CircleStyle({
    radius: 5,
    fill: new Fill({ color: '#ff6600' }),
    stroke: new Stroke({ color: '#fff', width: 2 }),
  }),
});

const vertexStyle = new Style({
  image: new CircleStyle({
    radius: 6,
    fill: new Fill({ color: '#ffffff' }),
    stroke: new Stroke({ color: '#ff6600', width: 2 }),
  }),
});

export function useMeasurementInteraction({ map, mapRef }: UseMeasurementInteractionOptions) {
  const dispatch = useAppDispatch();
  const activeMode = useAppSelector((state) => state.measurement.activeMode);
  const distancePoints = useAppSelector((state) => state.measurement.distancePoints);
  const areaPoints = useAppSelector((state) => state.measurement.areaPoints);
  const distanceUnits = useAppSelector((state) => state.measurement.distanceUnits);
  const areaUnits = useAppSelector((state) => state.measurement.areaUnits);
  const digitizationMode = useAppSelector((state) => state.digitization?.mode ?? 'idle');
  const activeDrawingTool = useAppSelector((state) => state.drawing?.activeTool ?? null);
  const isSplitToolActive = useAppSelector(
    (state) => state.geometryEditing?.isSplitToolActive ?? false
  );
  const isEditingGeometry = useAppSelector(
    (state) => state.geometryEditing?.isEditingGeometry ?? false
  );
  const liveMap = map ?? mapRef?.current ?? null;

  const drawInteractionRef = useRef<Draw | null>(null);
  const measureLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const measureSourceRef = useRef<VectorSource | null>(null);
  const pointerMoveListenerRef = useRef<((evt: MapBrowserEvent<PointerEvent>) => void) | null>(
    null
  );
  const clickListenerRef = useRef<((evt: MapBrowserEvent<PointerEvent>) => void) | null>(null);
  const distanceUnitsRef = useRef(distanceUnits);
  const areaUnitsRef = useRef(areaUnits);

  useEffect(() => {
    distanceUnitsRef.current = distanceUnits;
    areaUnitsRef.current = areaUnits;
  }, [distanceUnits, areaUnits]);

  // Activating measurement is a centralized transition away from competing modes.
  useEffect(() => {
    if (!activeMode) return;
    dispatch(stopDigitization());
    dispatch(setDrawingTool(null));
    dispatch(deactivateSplitTool());
    dispatch(deactivateNodeEditing());
  }, [activeMode, dispatch]);

  const cleanup = useCallback((targetMap: Map | null) => {
    if (!targetMap) return;
    if (drawInteractionRef.current) {
      targetMap.removeInteraction(drawInteractionRef.current);
      drawInteractionRef.current = null;
    }
    if (measureLayerRef.current) {
      targetMap.removeLayer(measureLayerRef.current);
      measureLayerRef.current = null;
    }
    measureSourceRef.current = null;
  }, []);

  const ownsMap =
    activeMode !== null &&
    digitizationMode === 'idle' &&
    activeDrawingTool === null &&
    !isSplitToolActive &&
    !isEditingGeometry;

  useEffect(() => {
    if (!liveMap) return;

    if (pointerMoveListenerRef.current) {
      liveMap.un('pointermove', pointerMoveListenerRef.current);
      pointerMoveListenerRef.current = null;
    }

    if (ownsMap) {
      const handlePointerMove = (evt: MapBrowserEvent<PointerEvent>) => {
        if (evt.dragging) return;
        dispatch(setCursorCoordinates(toLonLat(evt.coordinate) as [number, number]));
      };
      liveMap.on('pointermove', handlePointerMove);
      pointerMoveListenerRef.current = handlePointerMove;
    } else {
      dispatch(setCursorCoordinates(null));
    }

    return () => {
      if (pointerMoveListenerRef.current) {
        liveMap.un('pointermove', pointerMoveListenerRef.current);
        pointerMoveListenerRef.current = null;
      }
    };
  }, [liveMap, ownsMap, dispatch]);

  useEffect(() => {
    if (!liveMap) return;

    if (clickListenerRef.current) {
      liveMap.un('singleclick', clickListenerRef.current);
      clickListenerRef.current = null;
    }

    if (ownsMap && activeMode === 'coordinate') {
      const handleClick = (evt: MapBrowserEvent<PointerEvent>) => {
        dispatch(setClickedCoordinate(toLonLat(evt.coordinate) as [number, number]));
      };
      liveMap.on('singleclick', handleClick);
      clickListenerRef.current = handleClick;
    }

    return () => {
      if (clickListenerRef.current) {
        liveMap.un('singleclick', clickListenerRef.current);
        clickListenerRef.current = null;
      }
    };
  }, [liveMap, ownsMap, activeMode, dispatch]);

  useEffect(() => {
    if (!liveMap) return;
    cleanup(liveMap);

    if (ownsMap && (activeMode === 'distance' || activeMode === 'area')) {
      const source = new VectorSource();
      measureSourceRef.current = source;

      const layer = new VectorLayer({
        source,
        style: [measurementStyle, vertexStyle],
        zIndex: 1000,
      });
      measureLayerRef.current = layer;
      liveMap.addLayer(layer);

      const draw = new Draw({
        source,
        type: activeMode === 'distance' ? 'LineString' : 'Polygon',
        style: measurementStyle,
      });

      draw.on('drawstart', () => source.clear());
      draw.on('drawend', (evt) => {
        const geometry = evt.feature.getGeometry();
        if (!geometry) return;

        if (activeMode === 'distance') {
          const coords = (geometry as LineString).getCoordinates() as Coordinate[];
          const lonLatCoords = coords.map((coord) => toLonLat(coord) as [number, number]);
          lonLatCoords.forEach((coord) => dispatch(addDistancePoint(coord)));
          dispatch(updateCurrentDistance(measureDistance(lonLatCoords, distanceUnitsRef.current)));
        } else {
          const coords = (geometry as Polygon).getCoordinates()[0] as Coordinate[];
          const lonLatCoords = coords.map((coord) => toLonLat(coord) as [number, number]);
          lonLatCoords.forEach((coord) => dispatch(addAreaPoint(coord)));
          dispatch(updateCurrentArea(measureArea(lonLatCoords, areaUnitsRef.current)));
          dispatch(
            updateCurrentPerimeter(measurePerimeter(lonLatCoords, distanceUnitsRef.current))
          );
        }

        dispatch(finishMeasurement());
      });

      liveMap.addInteraction(draw);
      drawInteractionRef.current = draw;
    }

    return () => cleanup(liveMap);
  }, [liveMap, ownsMap, activeMode, cleanup, dispatch]);

  useEffect(() => {
    if (distancePoints.length >= 2) {
      dispatch(updateCurrentDistance(measureDistance(distancePoints, distanceUnits)));
    }
  }, [distanceUnits, distancePoints, dispatch]);

  useEffect(() => {
    if (areaPoints.length >= 3) {
      dispatch(updateCurrentArea(measureArea(areaPoints, areaUnits)));
      dispatch(updateCurrentPerimeter(measurePerimeter(areaPoints, distanceUnits)));
    }
  }, [areaUnits, areaPoints, distanceUnits, dispatch]);
}
